import React, { useState, useMemo } from 'react';
import { Eye } from 'lucide-react';
import { DataTable, TableActionButton } from "@/components/ui";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";
import ConfirmationModal from '../modals/ConfirmationModal';
import DesignApprovalControlPanel from '../components/DesignApprovalControlPanel';
import api from '../../../../services/api';
import { showApiError, showSuccessMessage } from '@/lib/toast';
import { useAuthStore } from "@/store/authStore";

export const DesignApprovalScreen = ({ leads, isLoading, onRefresh, onDesignClick }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const [localFilters, setLocalFilters] = useState({
    date: 'all',
    fromDate: '',
    toDate: ''
  });

  const { user } = useAuthStore();
  const uRole = (user?.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
  const isProjectManager = ["project-manager", "pm"].includes(uRole);
  const isFieldOfficer = ["field-officer", "fo"].includes(uRole);
  const isAccountant = ["accountant", "account"].includes(uRole);

  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    
    let result = leads.filter(l => l.designApproval && l.designApproval.isRequested);

    if (localSearch) {
      const lowerSearch = localSearch.toLowerCase();
      result = result.filter(l => 
        l.customId?.toLowerCase().includes(lowerSearch) ||
        l.customerName?.toLowerCase().includes(lowerSearch) ||
        l.firstName?.toLowerCase().includes(lowerSearch) ||
        l.email?.toLowerCase().includes(lowerSearch)
      );
    }

    const { date, fromDate, toDate } = localFilters;
    if (date !== 'all' || fromDate || toDate) {
      let startDate = null;
      let endDate = null;
      
      const now = new Date();
      if (date && date !== 'all') {
        startDate = new Date();
        switch(date) {
          case 'today':
            startDate.setHours(0,0,0,0);
            break;
          case 'this_week':
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'this_month':
          case 'month':
            startDate.setDate(now.getDate() - 30);
            break;
          case 'this_year':
          case 'year':
            startDate.setDate(now.getDate() - 365);
            break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter(l => {
          const createdAt = new Date(l.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [leads, localSearch, localFilters]);

  const handleRoleSelect = (role, lead) => {
    setSelectedRole(role);
    setSelectedLead(lead);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLead(null);
  };

  const handleConfirmRole = async ({ status, notes }) => {
    if (!selectedLead || !selectedRole) return;
    
    setActionLoading(true);
    try {
      const roleKey = selectedRole === 'Designer' ? 'designer' : 'mainDesigner';
      const currentApproval = selectedLead.designApproval || {};
      
      const updatedApproval = {
        ...currentApproval,
        [roleKey]: {
          status,
          notes,
          actedAt: new Date()
        }
      };

      await api.patch(`/verve-leads/${selectedLead._id}`, {
        designApproval: updatedApproval
      });
      
      showSuccessMessage(`${selectedRole} ${status === 'approved' ? 'Approved' : 'Rejected'} successfully`);
      setIsModalOpen(false);
      setSelectedLead(null);
      
      if (onRefresh) {
        onRefresh();
      } else {
        window.location.reload();
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const getApprovalStatus = (approval) => {
    if (!approval || !approval.isRequested) return { text: "Not Requested", color: "text-slate-400 bg-slate-100" };
    
    const { designer, mainDesigner } = approval;
    if (mainDesigner?.status === 'approved') return { text: "Fully Approved", color: "text-green-700 bg-green-100" };
    if (mainDesigner?.status === 'rejected') return { text: "Rejected by Main Designer", color: "text-red-700 bg-red-100" };
    if (designer?.status === 'rejected') return { text: "Rejected by Designer", color: "text-red-700 bg-red-100" };
    if (designer?.status === 'approved') return { text: "Pending Main Designer", color: "text-blue-700 bg-blue-100" };
    
    return { text: "Requested", color: "text-blue-700 bg-blue-100" };
  };

  const columns = useMemo(() => [
    {
      header: "Verve ID",
      key: "customId",
      minWidth: "170px",
      render: (l) => (
        <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
          {l.customId || "—"}
        </span>
      )
    },
    {
      header: "Contact Details",
      key: "customerName",
      render: (l) => {
        const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
        return (
          <div className="flex flex-col min-w-0 py-1">
            <span className="text-brand-dark tracking-tight text-[13px] font-medium leading-tight truncate">
              {fullName}
            </span>
            <span className="text-[11px] text-slate-500 truncate flex items-center mt-0.5 tracking-tighter">
              {l.phoneNumber || l.phone || "No Phone"}
              {l.email && <span className="mx-1.5 opacity-40">|</span>}
              {l.email && l.email.toLowerCase()}
            </span>
          </div>
        );
      }
    },
    {
      header: "Status",
      key: "status",
      render: (l) => {
        const status = getApprovalStatus(l.designApproval);
        return (
          <span className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider ${status.color}`}>
            {status.text}
          </span>
        );
      }
    },
    {
      header: "Actions",
      key: "actions",
      render: (l) => {
        const approval = l.designApproval || {};
        const { designer, mainDesigner, isRequested } = approval;

        if (!isRequested) {
          return <span className="text-xs text-slate-400 italic">No request sent</span>;
        }

        const isRejected = designer?.status === 'rejected' || mainDesigner?.status === 'rejected';
        const isFullyApproved = mainDesigner?.status === 'approved';

        if (isFullyApproved) {
          return (
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">Already Approved</span>
              <TableActionButton
                icon={Eye}
                variant="primary"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  if (onDesignClick) onDesignClick(l);
                }}
                title="View Design Details"
              />
            </div>
          );
        }

        if (isRejected) {
          return <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">Approval Rejected</span>;
        }

        // Logic for which button to show:
        const showDesigner = designer?.status === 'pending';
        const showMainDesigner = designer?.status === 'approved' && mainDesigner?.status === 'pending';

        return (
          <div className="flex items-center gap-2">
            {showDesigner && (
              <button
                onClick={(e) => { e.stopPropagation(); handleRoleSelect('Designer', l); }}
                className="px-3 py-1.5 bg-yellow-500 text-white text-[11px] font-semibold rounded hover:bg-yellow-600 transition-colors shadow-sm"
              >
                Designer
              </button>
            )}
            {showMainDesigner && (
              <button
                onClick={(e) => { e.stopPropagation(); handleRoleSelect('Main Designer', l); }}
                className="px-3 py-1.5 bg-green-500 text-white text-[11px] font-semibold rounded hover:bg-green-600 transition-colors shadow-sm"
              >
                Main Designer
              </button>
            )}
          </div>
        );
      }
    }
  ], []);

  return (
    <div className="flex flex-col gap-4">
      {(!isProjectManager && !isFieldOfficer && !isAccountant) && (
        <DesignApprovalControlPanel
          search={localSearch}
          setSearch={setLocalSearch}
          filters={localFilters}
          setFilters={setLocalFilters}
        />
      )}

      <div className="w-full h-full bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredLeads}
          isLoading={isLoading}
          backgroundImage={TABLE_BACKGROUNDS?.leads}
          className="border-none shadow-none rounded-none"
        />

        <ConfirmationModal 
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmRole}
          role={selectedRole}
          loading={actionLoading}
        />
      </div>
    </div>
  );
};

export default DesignApprovalScreen;
